import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';

import { usePlayer } from '@hooks/query/usePlayer';
import { useUserQuery } from '@hooks/query/useUserQuery';
import {
  BROADCAST_CHANNEL,
  useBroadcastChannel,
} from '@hooks/useBroadcastChannel';
import { useModal } from '@hooks/useModal';
import { useScrollPosition } from '@hooks/useScrollPosition';
import { FontSizes, Radius, Spacing, Typography } from 'chatie-design-system';

import { media } from '@styles/css';
import { Select } from 'chatie-design-system/form';

import Empty from '@components/common/Empty';
import { PaginationIterator } from '@components/common/Pagination';
import CharacterDeleteModal from '@components/modal/CharacterDeleteModal';

import {
  CharacterOrderType,
  deletePlaySet,
  fetchCharacters,
  fetchMyCharacters,
  fetchSituations,
} from '@api/chatbot';
import { Platform, QUERY_KEYS, ReportObjectType } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { IChatCharacter } from '@model';
import { formatComma, toQueryString } from '@utils/misc';
import { useChatPaginationQuery } from '@utils/query';
import { useWebView } from '@utils/webview';

import CharacterAddCard from './CharacterAddCard';
import PlayCountCard from './PlayCountCard';
import ProfileInfoCard, { ProfileInfoCardType } from './ProfileInfoCard';

const containerCSS = () => css`
  padding: 0 ${Spacing.medium}px;
  ${media.tablet} {
    padding: 0 ${Spacing.xlarge}px;
  }
  ${media.desktop} {
    padding: 0 ${Spacing.xxlarge}px;
  }

  .cheming-stat-container {
    margin-top: 9px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    ${Typography.body14};
    gap: 3px;
    margin-bottom: 21px;
    .comment-count {
      ${Typography.body12};
      color: #90949b;
    }
  }
`;

const filterHeaderCSS = (isCheming?: boolean) => (theme: Theme) =>
  css`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    margin-top: ${Spacing.xxsmall}px;
    margin-bottom: 10px;

    .dropdown {
      > div {
        ${isCheming ? 'color:#7F00FF' : ''};
        border: 1px solid ${theme.var.gray300};
        border-radius: ${Radius.medium}px;
        height: 35px;
        padding: ${Spacing.xsmall}px;
        background-color: ${theme.var.white};
      }
      > ul {
        z-index: 20;
      }
    }
    .count {
      ${Typography.body12};
    }
  `;
const iteratorCSS = css`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  column-gap: ${Spacing.small}px;
  row-gap: ${Spacing.small}px;
  ${media.tablet} {
    grid-template-columns: repeat(3, 1fr);
  }
  ${media.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`;
interface CharacterCardListProps {
  userId: number;
  isMyPage: boolean;
}

const CharacterCardList = ({ userId, isMyPage }: CharacterCardListProps) => {
  const { t } = useTranslation();
  const { platform, isCheming } = useGlobalContext();
  const { web } = useWebView();

  const router = useRouter();
  const queryClient = useQueryClient();

  const options = [
    { label: '최신순', value: 'id' },
    { label: '인기순', value: 'stat__message_count' },
  ];
  const [orderValue, setOrderValue] = useState(options[0]);
  const [selectedCharacter, setSelectedCharacter] =
    useState<IChatCharacter | null>(null);
  const [isDeleteAction, setIsDeleteAction] = useState(false);

  const { useFetchUser } = useUserQuery();
  const { data: user } = useFetchUser(userId);

  const { data: player } = usePlayer(userId, {
    refetchOnWindowFocus: isCheming ? false : 'always',
  });

  const myCharacterQueryKey = QUERY_KEYS.CHARACTER.MY_CHARACTER_LIST(
    orderValue.value as CharacterOrderType
  );

  const myCharacterQuery = useChatPaginationQuery(
    myCharacterQueryKey,
    ({ pageParam }) =>
      fetchMyCharacters(
        { orderBy: orderValue.value as CharacterOrderType },
        pageParam
      ),
    {
      enabled: isMyPage,
    }
  );

  const userCharacterQuery = useChatPaginationQuery(
    QUERY_KEYS.CHARACTER.USERS(userId, orderValue.value as CharacterOrderType),
    ({ pageParam }) =>
      fetchCharacters(
        {
          user_id: userId,
          order_by: orderValue.value as CharacterOrderType,
          status: 'PUB',
        },
        pageParam
      ),
    {
      enabled: !isMyPage,
    }
  );

  const { mutate: deleteCharacter } = useMutation(
    (characterId: number) => deletePlaySet(characterId),
    {
      onSettled: () => {
        closeCharacterDeleteModal();
        alert('캐릭터 삭제가 완료되었습니다.');
        postChannelMessage('delete');
        QUERY_KEYS.CHAT_ROOM.LIST('Library');
        queryClient.invalidateQueries(QUERY_KEYS.CHAT_ROOM.LIST('Library'));
        queryClient.invalidateQueries(
          QUERY_KEYS.CHARACTER.MY_CHARACTER_LIST(
            orderValue.value as CharacterOrderType
          )
        );

        setSelectedCharacter(null);
      },
    }
  );

  const [
    isCharacterDeleteModalOpen,
    { open: openCharacterDeleteModal, close: closeCharacterDeleteModal },
  ] = useModal('character-delete');

  useScrollPosition(`userCharacterCardList:${userId}`);

  useBroadcastChannel<string>(BROADCAST_CHANNEL.CHARACTER_UPDATE, () => {
    queryClient.invalidateQueries(myCharacterQueryKey);
  });

  useBroadcastChannel<string>(BROADCAST_CHANNEL.CHARACTER_DELETE, () => {
    queryClient.invalidateQueries(myCharacterQueryKey);
  });

  const { postChannelMessage } = useBroadcastChannel<string>(
    BROADCAST_CHANNEL.CHARACTER_DELETE
  );

  const handleSelectCharacter = (character: IChatCharacter) => {
    setSelectedCharacter(character);
  };

  const handleCreateChat = async (characterId: number) => {
    const data = await fetchSituations({
      character_id: characterId,
    });
    if (data && data.items[0].id) {
      if (platform === Platform.web) {
        router.push(`/play/situations/${data.items[0].id}/chat-room`);
      } else {
        web.openPage(`/play/situations/${data.items[0].id}/chat-room`);
      }
    }
  };

  const handleNavigateCreate = () => {
    if (platform === Platform.web) {
      router.push(`/play/characters/create`);
    }
    web.openPage(`/play/characters/create`);
  };

  const handleNavigateEdit = (character: IChatCharacter) => {
    const type = character?.can_have_visual ? 'visual' : 'default';
    if (platform === Platform.web) {
      router.push(
        `/play/characters/${character.id}/edit?characterType=${type}`
      );
    }
    web.openPage(`/play/characters/${character.id}/edit?characterType=${type}`);
  };

  const handleNavigateChemingEdit = (character: IChatCharacter) => {
    if (platform === Platform.web) {
      router.push(`/play/characters/${character.id}/edit`);
    }
    web.openPage(`/play/characters/${character.id}/edit`);
  };

  const handleConfirmDeleteCharacter = (characterId: number) => {
    deleteCharacter(characterId);
    setSelectedCharacter(null);
  };

  const handleActionSheet = (value: ProfileInfoCardType) => {
    if (selectedCharacter) {
      const type = selectedCharacter?.can_have_visual ? 'visual' : 'default';

      switch (value) {
        case 'modify':
          setIsDeleteAction(false);
          if (platform === Platform.web) {
            router.push(
              `/play/characters/${selectedCharacter.id}/edit?characterType=${type}`
            );
          }
          web.openPage(
            `/play/characters/${selectedCharacter.id}/edit?characterType=${type}`
          );
          break;

        case 'delete':
          if (selectedCharacter.status === 'DRF') {
            if (confirm('정말 삭제하시겠습니까?')) {
              handleConfirmDeleteCharacter(selectedCharacter.id);
            }
          } else {
            setIsDeleteAction(true);
            openCharacterDeleteModal();
          }
          break;

        case 'detail':
          setIsDeleteAction(false);
          if (platform === Platform.web) {
            router.push(`/play/characters/${selectedCharacter.id}`);
          }
          web.openPage(`/play/characters/${selectedCharacter.id}`);
          break;

        case 'report':
          if (platform === Platform.web) {
            router.push(
              `/report${toQueryString({
                objectType: ReportObjectType.character,
                object_id: selectedCharacter.id,
              })}`
            );
          }
          web.openPage(
            `/report${toQueryString({
              objectType: ReportObjectType.character,
              object_id: selectedCharacter.id,
            })}`
          );

          break;

        default:
          break;
      }
    }
  };

  return (
    <div css={containerCSS}>
      <PlayCountCard isMyPage={isMyPage} user={user} player={player} />

      <div css={filterHeaderCSS(isCheming)}>
        <Select
          className="dropdown"
          name="order"
          fontSize={FontSizes.small}
          options={options}
          selected={orderValue}
          onChange={(values) => setOrderValue(values)}
        />
        <p className={'count'}>
          {t('{{count}}개의 캐릭터', {
            count: isMyPage
              ? formatComma(myCharacterQuery?.totalCount)
              : formatComma(userCharacterQuery?.totalCount),
          })}
        </p>
      </div>

      {isMyPage ? (
        <PaginationIterator
          name="my-character-card-list"
          query={myCharacterQuery}
          css={iteratorCSS}
          ad={
            isMyPage && (
              <CharacterAddCard
                handleOnClick={handleNavigateCreate}
                isCheming={isCheming}
              />
            )
          }
        >
          {(item) => (
            <ProfileInfoCard
              data={item}
              handleActionSheet={handleActionSheet}
              handleSelectCharacter={handleSelectCharacter}
              isMyPage={isMyPage}
              size={'w166'}
              handleCreateChat={handleCreateChat}
              handleNavigateEdit={
                isCheming ? handleNavigateChemingEdit : handleNavigateEdit
              }
            />
          )}
        </PaginationIterator>
      ) : (
        <PaginationIterator
          name="user-character-card-list"
          query={userCharacterQuery}
          css={iteratorCSS}
        >
          {(item) => (
            <ProfileInfoCard
              data={item}
              handleActionSheet={handleActionSheet}
              handleSelectCharacter={handleSelectCharacter}
              isMyPage={false}
              size={'w166'}
              handleCreateChat={handleCreateChat}
              handleNavigateEdit={
                isCheming ? handleNavigateChemingEdit : handleNavigateEdit
              }
            />
          )}
        </PaginationIterator>
      )}
      {!isMyPage && (
        <Empty query={userCharacterQuery}>
          <p>{t('아직 캐릭터가 없습니다.')}</p>
        </Empty>
      )}
      {isDeleteAction && selectedCharacter && (
        <CharacterDeleteModal
          open={isCharacterDeleteModalOpen}
          onClose={closeCharacterDeleteModal}
          handleOnConfirm={handleConfirmDeleteCharacter}
          character={selectedCharacter}
        />
      )}
    </div>
  );
};

export default CharacterCardList;
